import * as React from 'react';
import { Home, Menu, Star, Calendar, Bell, AlertTriangle, HelpCircle } from 'lucide-react';

export type SideNavButtonIcon = 'side-menu' | 'home' | 'favourite' | 'calendar' | 'notifications' | 'emergency-alert' | 'help';

export interface SideNavButtonProps {
  /**
   * The icon to display
   */
  icon: SideNavButtonIcon;
  /**
   * Whether the button is selected/active
   */
  selected?: boolean;
  /**
   * Whether the button is focused (shows green ring)
   */
  focused?: boolean;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Accessible label for the button
   */
  'aria-label'?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
}

/**
 * SideNavButton component - Arbor Design System
 *
 * A circular navigation button used in the side navigation bar.
 * Supports different icons and states (default, hover, active, selected, focused).
 */
export const SideNavButton = React.forwardRef<HTMLButtonElement, SideNavButtonProps>(
  (
    {
      icon,
      selected = false,
      focused = false,
      onClick,
      'aria-label': ariaLabel,
      className,
      style,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isActive, setIsActive] = React.useState(false);

    // Determine icon color based on state
    const getIconColor = () => {
      if (selected) return '#0e8a0e'; // Green for selected
      if (isActive || isHovered) return '#2f2f2f'; // Dark gray for hover/active
      return '#7e7e7e'; // Gray for default
    };

    // Determine background color based on state
    const getBackgroundColor = () => {
      if (selected) return '#f0faf3'; // Light green for selected
      if (isActive) return '#efefef'; // Darker gray for active
      if (isHovered) return '#f8f8f8'; // Light gray for hover
      return 'transparent'; // Transparent for default
    };

    const buttonStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '42px',
      height: '42px',
      borderRadius: '99px',
      border: 'none',
      backgroundColor: getBackgroundColor(),
      cursor: 'pointer',
      padding: 0,
      outline: 'none',
      transition: 'background-color 150ms ease, box-shadow 150ms ease',
      boxShadow: focused ? '0px 0px 0px 3px #3cad51' : 'none',
      ...style,
    };

    // Get the appropriate icon component
    const renderIcon = () => {
      const iconColor = getIconColor();
      const iconSize = 20;
      const strokeWidth = 2;

      // All icons are stroke-only (outline), no fill - just color changes
      const iconProps = {
        size: iconSize,
        color: iconColor,
        strokeWidth,
      };

      switch (icon) {
        case 'side-menu':
          return <Menu {...iconProps} />;
        case 'home':
          return <Home {...iconProps} />;
        case 'favourite':
          return <Star {...iconProps} />;
        case 'calendar':
          return <Calendar {...iconProps} />;
        case 'notifications':
          return <Bell {...iconProps} />;
        case 'emergency-alert':
          return <AlertTriangle {...iconProps} />;
        case 'help':
          return <HelpCircle {...iconProps} />;
        default:
          return null;
      }
    };

    // Default aria-label based on icon
    const defaultAriaLabel = icon.charAt(0).toUpperCase() + icon.slice(1);

    return (
      <button
        ref={ref}
        type="button"
        className={className}
        style={buttonStyles}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsActive(false);
        }}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        aria-label={ariaLabel || defaultAriaLabel}
        aria-pressed={selected}
      >
        {renderIcon()}
      </button>
    );
  }
);

SideNavButton.displayName = 'SideNavButton';
